import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Palette } from 'lucide-react';
import { PROTOTYPE_DESIGNS, getPrototypeDesign, type PrototypeDto } from '@aiai/shared';
import { createPrototype, fetchSettingsStatus } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { useOrg } from '../org/useOrg';
import { DesignPicker, DesignSwatch } from './DesignPicker';
import { Modal } from './Modal';
import { Spinner } from './Spinner';
import { WorkspaceSelectField } from './WorkspaceSelectField';

const MIN_DESCRIPTION_LENGTH = 10;

export function NewPrototypeModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Receives the new prototype and the slug of the workspace it was created in. */
  onCreated?: (prototype: PrototypeDto, workspaceSlug: string) => void;
}) {
  const queryClient = useQueryClient();
  const { activeOrg } = useOrg();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [designId, setDesignId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const design = designId ? getPrototypeDesign(designId) : undefined;

  const { data: settings } = useQuery({
    queryKey: qk.settings,
    queryFn: fetchSettingsStatus,
  });
  // A token is "present" when the workspace configured its own OR the platform
  // provides a shared one (basic plan uses the shared credential rail), so a
  // basic workspace with platform tokens is ready without any local setup.
  const missingClaude = settings
    ? !settings['CLAUDE_CODE_OAUTH_TOKEN'] && !settings['platformClaudeTokenAvailable']
    : false;
  const missingGithub = settings
    ? !settings['GITHUB_TOKEN'] && !settings['platformGithubAppAvailable']
    : false;

  const reset = () => {
    setTitle('');
    setDescription('');
    setDesignId(null);
  };

  const mutation = useMutation({
    mutationFn: () => createPrototype({ title, description, designId }),
    onSuccess: (prototype) => {
      queryClient.invalidateQueries({ queryKey: qk.prototypes });
      // Refresh the cross-workspace feeds (Prototypes/Overview) too.
      queryClient.invalidateQueries({ queryKey: qk.me });
      // The prototype was created in the active workspace (the selector's value).
      const workspaceSlug = activeOrg?.slug ?? '';
      reset();
      onCreated?.(prototype, workspaceSlug);
      onClose();
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const canSubmit =
    title.trim().length > 0 &&
    description.trim().length >= MIN_DESCRIPTION_LENGTH &&
    !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New prototype"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
          >
            {mutation.isPending && <Spinner />}
            Create prototype
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {/* Which workspace this prototype is created in. */}
        <WorkspaceSelectField capability="prototype:manage" active={open} />

        {(missingClaude || missingGithub) && (
          <div className="rounded-[5px] border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
            <div className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
              <div className="space-y-1">
                <p className="font-medium">Setup required before running</p>
                {missingClaude && (
                  <p>
                    Claude subscription token not configured —{' '}
                    <Link to="/settings" className="underline" onClick={onClose}>
                      add it in Settings
                    </Link>
                    .
                  </p>
                )}
                {missingGithub && (
                  <p>
                    GitHub token not configured —{' '}
                    <Link to="/settings" className="underline" onClick={onClose}>
                      add it in Settings
                    </Link>
                    .
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="label" htmlFor="pp-title">
            Title
          </label>
          <input
            id="pp-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Onboarding flow redesign"
            maxLength={100}
            autoFocus
          />
        </div>

        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <label className="label" htmlFor="pp-description">
              Description
            </label>
            <span className="text-xs text-ink-faint" aria-live="polite">
              {description.length} / 4000
            </span>
          </div>
          <textarea
            id="pp-description"
            className="input min-h-28"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this prototype should do…"
            maxLength={4000}
          />
          {description.length > 0 && description.trim().length < MIN_DESCRIPTION_LENGTH && (
            <p className="mt-1 text-xs text-ink-faint">
              Minimum {MIN_DESCRIPTION_LENGTH} characters — {MIN_DESCRIPTION_LENGTH - description.trim().length} more needed.
            </p>
          )}
        </div>

        <div>
          <span className="label">Design style</span>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex w-full items-center gap-3 rounded-[6px] border border-line p-2 text-left transition-colors hover:border-ink-faint"
            aria-haspopup="dialog"
          >
            {design ? (
              <>
                <div className="w-16 flex-none overflow-hidden rounded-[5px] border border-line">
                  <DesignSwatch design={design} compact />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink">{design.name}</p>
                  <p className="truncate text-xs text-ink-faint">{design.tagline}</p>
                </div>
              </>
            ) : (
              <>
                <div className="flex h-9 w-16 flex-none items-center justify-center rounded-[5px] border border-dashed border-line">
                  <Palette className="h-4 w-4 text-ink-faint" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink">No preference</p>
                  <p className="truncate text-xs text-ink-faint">
                    Pick one of {PROTOTYPE_DESIGNS.length} styles — or let the agent choose one that fits.
                  </p>
                </div>
              </>
            )}
            <span className="btn-secondary pointer-events-none flex-none">
              {design ? 'Change' : 'Browse'}
            </span>
          </button>
        </div>

        {mutation.isError && (
          <div className="rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
            {errorMessage(mutation.error, 'Could not create prototype')}
          </div>
        )}
      </div>

      <DesignPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={designId}
        onSelect={setDesignId}
      />
    </Modal>
  );
}
